import { useState } from 'react';
import styled from '@emotion/styled';
import { Timestamp, TimestampType } from '@britishcouncil/common-core';
import {
  DateRangeFilter,
  FiltersBar,
  MultiSelectFilter,
  MultiToggleFilter,
  SimpleFiltersObjectValues,
} from '@britishcouncil/react-admin';
import {
  ExamTimeOfDay,
  TestCentreOverviewAvailableDistrictDto,
  TestCentreOverviewVenueDto,
} from 'api/planning';
import { TestCentreOverviewQueryFilters } from 'store/test-centre-overview/service';

const WIDE_DROPDOWN_STYLE = { minWidth: '200px' };

export type TestCentreOverviewFilters = {
  testDate: DateRangeFilter;
  district: MultiSelectFilter<number>;
  venue: MultiSelectFilter<number>;
  sessionTime: MultiToggleFilter<ExamTimeOfDay>;
};

export type TestCentreOverviewFilterValues = SimpleFiltersObjectValues<TestCentreOverviewFilters>;

const DEFAULT_DATE_RANGE_DAYS = 90;

const sessionTimeOptions = [
  { text: 'Morning', value: ExamTimeOfDay.Morning },
  { text: 'Afternoon', value: ExamTimeOfDay.Afternoon },
  { text: 'Evening', value: ExamTimeOfDay.Evening },
];

export const getDefaultTestDateRange = (): [TimestampType, TimestampType] => {
  const today = Timestamp.now();
  return [today, Timestamp.addDays(DEFAULT_DATE_RANGE_DAYS)(today)];
};

export const getTestCentreOverviewFiltersInitialState = (
  locationId?: number
): TestCentreOverviewFilterValues => ({
  testDate: getDefaultTestDateRange(),
  district: locationId === undefined ? [] : [locationId],
  venue: [],
  sessionTime: [],
});

export const mapFiltersToQuery = (
  values: TestCentreOverviewFilterValues,
  locationId?: number
): TestCentreOverviewQueryFilters => {
  const [fromDate, toDate] = values.testDate ?? [];

  return {
    fromDate: fromDate ? Timestamp.toISODate(fromDate) : undefined,
    toDate: toDate ? Timestamp.toISODate(toDate) : undefined,
    districtIds: locationId === undefined ? values.district : [locationId],
    venueIds: values.venue,
    sessionTimes: values.sessionTime,
  };
};

export const TestCentreOverviewFilterBar = ({
  districts,
  venues,
  locationId,
  onFiltersChange,
  onFiltersApply,
  onFiltersClear,
}: {
  districts: TestCentreOverviewAvailableDistrictDto[];
  venues: TestCentreOverviewVenueDto[];
  locationId?: number;
  onFiltersChange: (values: TestCentreOverviewFilterValues) => void;
  onFiltersApply: (values: TestCentreOverviewFilterValues) => void;
  onFiltersClear: (values: TestCentreOverviewFilterValues) => void;
}) => {
  const [values, setValues] = useState<TestCentreOverviewFilterValues>(() =>
    getTestCentreOverviewFiltersInitialState(locationId)
  );

  const filters: TestCentreOverviewFilters = {
    testDate: {
      type: 'daterange',
      label: 'Test date',
    },
    district: {
      type: 'multiselect',
      label: 'Location',
      options: districts.map((district) => ({ text: district.name, value: district.id })),
      dropdownOptions: {
        disabled: locationId !== undefined,
        placeholder: 'All locations',
        clearable: true,
        search: true,
        style: WIDE_DROPDOWN_STYLE,
      },
    },
    venue: {
      type: 'multiselect',
      label: 'Venue',
      options: venues.map((venue) => ({ text: venue.name, value: venue.id })),
      dropdownOptions: {
        placeholder: 'All venues',
        clearable: true,
        search: true,
        style: WIDE_DROPDOWN_STYLE,
      },
    },
    sessionTime: {
      type: 'multitoggle',
      label: 'Session time',
      options: sessionTimeOptions,
      unselectable: true,
    },
  };

  const handleFiltersChange = (nextValues: TestCentreOverviewFilterValues) => {
    setValues(nextValues);
    onFiltersChange(nextValues);
  };

  const handleFiltersClear = (nextValues: TestCentreOverviewFilterValues) => {
    setValues(nextValues);
    onFiltersChange(nextValues);
    onFiltersClear(nextValues);
  };

  return (
    <S.Wrapper>
      <FiltersBar
        expanded
        clearable
        filters={filters}
        defaultValues={getTestCentreOverviewFiltersInitialState(locationId)}
        value={values}
        onFiltersChange={handleFiltersChange}
        onFiltersApply={onFiltersApply}
        onFiltersClear={handleFiltersClear}
        searchButtonText="Apply filters"
      />
    </S.Wrapper>
  );
};

const S = {
  Wrapper: styled.div`
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 4px;
    background: #fff;

    .ui.segment {
      margin: 0;
      border: none;
      box-shadow: none;
    }
  `,
};
